import { TILE_SIZES, type TileSize } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';

import {
    INITIAL_EDITORIAL_COLLECTION_POSITIONS,
    LOAD_MORE_EDITORIAL_COLLECTION_POSITIONS,
    MOBILE_INITIAL_EDITORIAL_COLLECTION_POSITIONS,
    MOBILE_ITEMS_PER_ROW,
    MOBILE_LOAD_MORE_EDITORIAL_COLLECTION_POSITIONS,
    MOBILE_LOAD_MORE_PERSONALIZED_COLLECTION_POSITIONS,
    MOBILE_PERSONALIZED_COLLECTION_POSITIONS,
    PERSONALIZED_COLLECTION_POSITIONS,
} from './constants.ts';
import { type HpSharedItemsGridItem, isCollectionItem } from './types.ts';

const ITEMS_PER_ROW = 4;

type ShimmerCollectionIndexOptions = {
    isMobile?: boolean;
    startIndex?: number;
};

function getShimmerCollectionPositions({
    isMobile = false,
    startIndex = 0,
}: ShimmerCollectionIndexOptions): Set<number> {
    if (isMobile && startIndex > 0) {
        return new Set(
            [
                ...MOBILE_LOAD_MORE_PERSONALIZED_COLLECTION_POSITIONS,
                ...MOBILE_LOAD_MORE_EDITORIAL_COLLECTION_POSITIONS,
            ].map(position => startIndex + position)
        );
    }

    if (isMobile) {
        return new Set([
            ...MOBILE_PERSONALIZED_COLLECTION_POSITIONS,
            ...MOBILE_INITIAL_EDITORIAL_COLLECTION_POSITIONS,
        ]);
    }

    if (startIndex > 0) {
        return new Set(
            LOAD_MORE_EDITORIAL_COLLECTION_POSITIONS.map(position => startIndex + position)
        );
    }

    return new Set([
        ...PERSONALIZED_COLLECTION_POSITIONS,
        ...INITIAL_EDITORIAL_COLLECTION_POSITIONS,
    ]);
}

const SHIMMER_ITEM_TYPES = {
    product: 'product',
    editorial: 'editorial',
    personalized: 'personalized',
} as const;

type ShimmerItemType = keyof typeof SHIMMER_ITEM_TYPES;

export function getShimmerItemType(
    index: number,
    options: ShimmerCollectionIndexOptions = {}
): ShimmerItemType {
    const collectionPositions = getShimmerCollectionPositions(options);

    if (collectionPositions.has(index)) {
        let personalizedPositions: readonly number[];

        if (options.isMobile) {
            personalizedPositions = options.startIndex
                ? MOBILE_LOAD_MORE_PERSONALIZED_COLLECTION_POSITIONS
                : MOBILE_PERSONALIZED_COLLECTION_POSITIONS;
        } else if (options.startIndex) {
            personalizedPositions = [];
        } else {
            personalizedPositions = PERSONALIZED_COLLECTION_POSITIONS;
        }

        const positionInSection = options.startIndex ? index - options.startIndex : index;

        if (personalizedPositions.includes(positionInSection)) {
            return SHIMMER_ITEM_TYPES.personalized;
        }

        return SHIMMER_ITEM_TYPES.editorial;
    }

    return SHIMMER_ITEM_TYPES.product;
}

export function isShimmerCollectionIndex(
    index: number,
    options: ShimmerCollectionIndexOptions = {}
): boolean {
    return getShimmerItemType(index, options) !== SHIMMER_ITEM_TYPES.product;
}

export function getCheckerboardTileSize(
    index: number,
    itemsPerRow: number = ITEMS_PER_ROW
): TileSize {
    const row = Math.floor(index / itemsPerRow);
    const col = index % itemsPerRow;

    return (row + col) % 2 === 0 ? TILE_SIZES.xLarge : TILE_SIZES.xxLarge;
}

type BuildProductTileSizeByGridSlotsOptions = {
    startIndex?: number;
    itemsPerRow?: number;
    isMobile?: boolean;
};

export function buildProductTileSizeByGridSlots(
    slots: ReadonlyArray<{ isCollection: boolean }>,
    {
        startIndex = 0,
        itemsPerRow = ITEMS_PER_ROW,
        isMobile = false,
    }: BuildProductTileSizeByGridSlotsOptions = {}
): Map<number, TileSize> {
    if (isMobile) {
        const sizes = new Map<number, TileSize>();

        slots.forEach((slot, offset) => {
            if (!slot.isCollection) {
                sizes.set(startIndex + offset, TILE_SIZES.small);
            }
        });

        return sizes;
    }

    // since there is no simple way to have a masonry layout which ends all columns on equal height
    // we keep track of small and large tiles in each column and balance them bellow
    const columnCounts = Array.from({ length: itemsPerRow }, () => ({
        small: 0,
        large: 0,
    }));
    const sizes = new Map<number, TileSize>();

    slots.forEach((slot, offset) => {
        const index = startIndex + offset;
        const col = index % itemsPerRow;

        if (slot.isCollection) {
            columnCounts[col].large += 1;
            return;
        }

        const counts = columnCounts[col];
        let size: TileSize;

        if (counts.large > counts.small) {
            size = TILE_SIZES.xLarge;
        } else if (counts.small > counts.large) {
            size = TILE_SIZES.xxLarge;
        } else {
            size = getCheckerboardTileSize(index, itemsPerRow);
        }

        if (size === TILE_SIZES.xLarge) {
            counts.small += 1;
        } else {
            counts.large += 1;
        }

        sizes.set(index, size);
    });

    return sizes;
}

function getSlotsCountForCompleteRows(
    slots: ReadonlyArray<{ isCollection: boolean }>,
    { isMobile, itemsPerRow = ITEMS_PER_ROW }: { isMobile: boolean; itemsPerRow?: number }
): number {
    if (!slots.length) {
        return 0;
    }

    if (isMobile) {
        // Mobile products pack 2 per row; collections span the full row (grid-column: 1 / -1).
        // Walk the slot list to find the last index that ends on a complete row boundary.
        let colInRow = 0;
        let lastCompleteCount = 0;

        for (let index = 0; index < slots.length; index += 1) {
            if (slots[index].isCollection) {
                colInRow = 0;
                lastCompleteCount = index + 1;
            } else {
                colInRow += 1;

                if (colInRow === MOBILE_ITEMS_PER_ROW) {
                    colInRow = 0;
                    lastCompleteCount = index + 1;
                }
            }
        }

        return lastCompleteCount;
    }

    return slots.length - (slots.length % itemsPerRow);
}

export function trimItemsToCompleteGridRows(
    items: HpSharedItemsGridItem[],
    { isMobile }: { isMobile: boolean }
): HpSharedItemsGridItem[] {
    const slots = items.map(item => ({ isCollection: isCollectionItem(item) }));
    const count = getSlotsCountForCompleteRows(slots, { isMobile });

    return items.slice(0, count);
}

type BuildDisplayProductTileSizeByIndexOptions = {
    shimmerCount?: number;
    isMobile: boolean;
};

export function buildDisplayProductTileSizeByIndex(
    items: HpSharedItemsGridItem[],
    { shimmerCount, isMobile }: BuildDisplayProductTileSizeByIndexOptions
): Map<number, TileSize> {
    const slots = items.map(item => ({ isCollection: isCollectionItem(item) }));

    if (shimmerCount) {
        const shimmerStartIndex = items.length;
        for (let index = 0; index < shimmerCount; index += 1) {
            slots.push({
                isCollection: isShimmerCollectionIndex(shimmerStartIndex + index, {
                    isMobile,
                    startIndex: shimmerStartIndex,
                }),
            });
        }
    }

    return buildProductTileSizeByGridSlots(slots, { isMobile });
}
